import { isFunction } from '@crown/framework/ir';
import { Signal } from '@crown/signal';
import type { ComponentFunction, OutletContent, OutletLoader, RouteConfig } from '../types';
import { isPromiseLike, moduleDefault } from './match';

/** Components and outlets already loaded for a path, keyed by path and by `path:outlet:name`. */
export type PrefetchCache = Map<string, OutletContent | null>;

/** The Network Information API, which the DOM typings do not describe. */
type NavigatorWithConnection = Navigator & {
  connection?: {
    effectiveType?: string;
    addEventListener: (type: string, listener: () => void) => void;
  };
};

export function createPrefetchSystem({
  resolveComponent,
  prefetchCache,
}: {
  resolveComponent: <T>(
    route: RouteConfig<T>,
    signal?: AbortSignal,
  ) => Promise<ComponentFunction<T> | null>;
  prefetchCache: PrefetchCache;
}) {
  const prefetchQueue = new Map<string, Promise<void>>();
  const prefetchAbortControllers = new Map<string, AbortController>();
  const networkState = new Signal.State('fast-3g');
  const idleState = new Signal.State(false);
  if ('connection' in navigator) {
    // SAFETY: `connection` was found on navigator just above, and where a browser has it, it is the Network Information API.
    const connection = (navigator as NavigatorWithConnection).connection;
    if (connection) {
      networkState.set(connection.effectiveType || 'unknown');
      connection.addEventListener('change', () => {
        networkState.set(connection.effectiveType || 'unknown');
      });
    }
  }
  if ('requestIdleCallback' in window) {
    let idleId: number | null = null;
    const checkIdle = () => {
      if (idleId != null && 'cancelIdleCallback' in window)
        window.cancelIdleCallback(idleId);
      idleState.set(false);
      if ('requestIdleCallback' in window) {
        idleId = window.requestIdleCallback(() => {
          idleState.set(true);
          setTimeout(checkIdle, 1000);
        });
      }
    };
    checkIdle();
    ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'].forEach(
      (event) => {
        document.addEventListener(event, checkIdle, { passive: true });
      },
    );
  }
  /**
   * Whether an outlet loads lazily, found by calling it: a loader hands back
   * a promise, and a component called with no context throws or builds
   * something at once.
   */
  function isLazyLoadingFunction(
    value: ComponentFunction<unknown> | OutletLoader,
  ): value is OutletLoader {
    if (!isFunction(value)) return false;
    try {
      // SAFETY: the probe calls every outlet with no arguments; a component that needs its context throws, which reads as not lazy.
      return isPromiseLike((value as OutletLoader)());
    } catch {
      return false;
    }
  }
  async function prefetchRoute(path: string, route: RouteConfig) {
    console.info('[Router] prefetchRoute', {
      path,
      alreadyQueued: prefetchQueue.has(path),
      alreadyCached: prefetchCache.has(path),
    });
    const queued = prefetchQueue.get(path);
    if (queued) return queued;
    if (prefetchCache.has(path)) return Promise.resolve();
    const abortController = new AbortController();
    prefetchAbortControllers.set(path, abortController);
    const prefetchPromise: Promise<void> = (async () => {
      try {
        const component = await resolveComponent(route, abortController.signal);
        prefetchCache.set(path, component);
        if (route.outlets) {
          for (const [outletName, outletValue] of Object.entries(
            route.outlets,
          )) {
            if (isLazyLoadingFunction(outletValue)) {
              try {
                const outletComponent = await outletValue();
                prefetchCache.set(
                  `${path}:outlet:${outletName}`,
                  moduleDefault(outletComponent),
                );
              } catch {}
            }
          }
        }
      } finally {
        prefetchQueue.delete(path);
        prefetchAbortControllers.delete(path);
      }
    })();
    prefetchQueue.set(path, prefetchPromise);
    return prefetchPromise;
  }
  function cancelPrefetch(path: string) {
    const ctrl = prefetchAbortControllers.get(path);
    if (ctrl) ctrl.abort();
    prefetchQueue.delete(path);
    prefetchAbortControllers.delete(path);
  }
  function cancelAllPrefetches() {
    for (const [, ctrl] of prefetchAbortControllers) ctrl.abort();
    prefetchQueue.clear();
    prefetchAbortControllers.clear();
  }
  function getPreloadContext(allRoutes: RouteConfig[], currentRoute: string) {
    return {
      routes: allRoutes,
      currentRoute,
      prefetchCache,
      isIdle: idleState.get(),
      isNetworkIdle:
        document.readyState === 'complete' && prefetchQueue.size === 0,
      callStackDepth: (new Error().stack || '').split('\n').length,
      networkType: networkState.get(),
    };
  }
  return {
    prefetchRoute,
    cancelPrefetch,
    cancelAllPrefetches,
    getPreloadContext,
  };
}
